import { useEffect, useState } from 'react';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Trash2, Loader2, BookOpen, Search, RotateCcw, Pencil, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const AdminEnrollments = () => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [courses, setCourses] = useState([]);
  const [filterCourse, setFilterCourse] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); loadCourses(); }, []);

  const load = async () => {
    try {
      const res = await api.get('/admin/enrollments');
      setEnrollments(res.data);
    } catch { toast.error('فشل تحميل البيانات'); }
    finally { setLoading(false); }
  };

  const loadCourses = async () => {
    try { const res = await api.get('/courses'); setCourses(res.data); } catch { /* noop */ }
  };

  const handleReset = async (rn, cid) => {
    if (!window.confirm('هل تريد إعادة ضبط تقدم هذا الاشتراك؟')) return;
    try { await api.delete(`/admin/enrollments/reset?reader_number_param=${rn}&course_id=${cid}`); toast.success('تم إعادة الضبط'); load(); }
    catch { toast.error('فشل إعادة الضبط'); }
  };

  const handleDelete = async (rn, cid) => {
    if (!window.confirm('هل تريد حذف هذا الاشتراك نهائياً؟')) return;
    try { await api.delete(`/admin/enrollments/delete?reader_number_param=${rn}&course_id=${cid}`); toast.success('تم الحذف'); load(); }
    catch { toast.error('فشل الحذف'); }
  };

  const openEdit = (e) => {
    setEditItem(e);
    setEditForm({
      reader_number: e.reader_number,
      course_id: e.course_id,
      status: e.status || 'active',
      enrolled_at: e.enrolled_at ? e.enrolled_at.split('T')[0] : '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      const params = new URLSearchParams({
        old_reader_number: editItem.reader_number,
        old_course_id: editItem.course_id,
        new_reader_number: editForm.reader_number,
        new_course_id: editForm.course_id,
        new_status: editForm.status,
        new_enrolled_at: editForm.enrolled_at,
      });
      await api.put(`/admin/enrollments/update?${params.toString()}`);
      toast.success('تم تحديث الاشتراك');
      setEditItem(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل التحديث'); }
    finally { setSaving(false); }
  };

  const getCourseName = (cid) => {
    const c = courses.find(co => co.id === cid || co.course_id === cid);
    return c?.title || c?.name || cid;
  };

  const filtered = enrollments.filter(e => {
    const matchSearch = !search ||
      (e.reader_name || '').includes(search) ||
      (e.reader_number || '').includes(search) ||
      getCourseName(e.course_id).includes(search);
    const matchCourse = !filterCourse || e.course_id === filterCourse;
    return matchSearch && matchCourse;
  });

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-16 h-16 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-enrollments-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-bold text-accent">اشتراكات المساقات</h1>
          <p className="text-muted-foreground mt-1">{enrollments.length} اشتراك</p>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute right-3 top-3 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="بحث بالاسم أو الرقم أو المساق..." className="pr-10" data-testid="search-enrollments" />
        </div>
        <select value={filterCourse} onChange={e => setFilterCourse(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white" data-testid="filter-course">
          <option value="">كل المساقات</option>
          {courses.map(c => <option key={c.id || c.course_id} value={c.id || c.course_id}>{c.title || c.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-muted-foreground">لا توجد اشتراكات</p>
        </Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="enrollments-table">
            <thead>
              <tr className="bg-primary/5 border-b-2 border-primary/20">
                <th className="text-right p-3 font-bold text-accent">الرقم القرائي</th>
                <th className="text-right p-3 font-bold text-accent">الاسم</th>
                <th className="text-right p-3 font-bold text-accent">المساق</th>
                <th className="text-right p-3 font-bold text-accent">تاريخ الاشتراك</th>
                <th className="text-right p-3 font-bold text-accent">الحالة</th>
                <th className="text-center p-3 font-bold text-accent">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr key={`${e.reader_number}_${e.course_id}_${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-muted/20'} data-testid={`enrollment-row-${i}`}>
                  <td className="p-3 font-mono text-sm">{e.reader_number}</td>
                  <td className="p-3 font-medium">{e.reader_name || '-'}</td>
                  <td className="p-3">{getCourseName(e.course_id)}</td>
                  <td className="p-3 text-muted-foreground text-xs">{e.enrolled_at ? new Date(e.enrolled_at).toLocaleDateString('ar-SA') : '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${e.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {e.status === 'active' ? 'نشط' : e.status || 'نشط'}
                    </span>
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)}
                        className="text-blue-500 hover:text-blue-700 hover:bg-blue-50" title="تعديل"
                        data-testid={`edit-btn-${i}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleReset(e.reader_number, e.course_id)}
                        className="text-orange-500 hover:text-orange-700 hover:bg-orange-50" title="إعادة ضبط التقدم"
                        data-testid={`reset-btn-${i}`}>
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(e.reader_number, e.course_id)}
                        className="text-red-500 hover:text-red-700 hover:bg-red-50" title="حذف"
                        data-testid={`delete-btn-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={!!editItem} onOpenChange={(open) => { if (!open) setEditItem(null); }}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="edit-enrollment-dialog">
          <DialogHeader>
            <DialogTitle className="text-accent">تعديل الاشتراك</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-semibold">الرقم القرائي</Label>
              <Input value={editForm.reader_number || ''} onChange={e => setEditForm({ ...editForm, reader_number: e.target.value })}
                className="mt-1" data-testid="edit-reader-number" />
            </div>
            <div>
              <Label className="text-sm font-semibold">المساق</Label>
              <select value={editForm.course_id || ''} onChange={e => setEditForm({ ...editForm, course_id: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white mt-1" data-testid="edit-course-id">
                {courses.map(c => <option key={c.id || c.course_id} value={c.id || c.course_id}>{c.title || c.name}</option>)}
                <option value={editForm.course_id}>{editForm.course_id}</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold">الحالة</Label>
              <select value={editForm.status || 'active'} onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm bg-white mt-1" data-testid="edit-status">
                <option value="active">نشط</option>
                <option value="paused">موقف</option>
                <option value="completed">مكتمل</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-semibold">تاريخ الاشتراك</Label>
              <Input type="date" value={editForm.enrolled_at || ''} onChange={e => setEditForm({ ...editForm, enrolled_at: e.target.value })}
                className="mt-1" data-testid="edit-enrolled-at" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleSaveEdit} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="save-edit-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin ml-1" /> : <Check className="w-4 h-4 ml-1" />}
                حفظ التعديلات
              </Button>
              <Button variant="outline" onClick={() => setEditItem(null)} className="flex-1" data-testid="cancel-edit-btn">
                <X className="w-4 h-4 ml-1" /> إلغاء
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEnrollments;
